import React, { useState, useEffect, useRef } from 'react';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import { SearchIcon, MoreHorizontalIcon, PlusIcon } from '../../utils/icons';
import WorkspaceHeader from './WorkspaceHeader';
import FilterNav from './FilterNav';
import WorkspaceList from './WorkspaceList';
import SongCell from '../ui/SongCell';
import SongCellLoading from '../ui/SongCellLoading';
import NewWorkspaceCell from '../ui/NewWorkspaceCell';

const WorkspaceCell = ({ title, songCount, timeAgo, image, onMenuClick, onClick }) => {
  const isNew = timeAgo === 'just now';

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex gap-3 items-center cursor-pointer hover:opacity-80 transition-opacity" onClick={onClick}>
        <div
          className="w-[60px] h-[50px] rounded-lg bg-cover bg-center flex-shrink-0"
          style={{
            backgroundColor: colors.bg.fog.thin,
            backgroundImage: image ? `url(${image})` : 'none'
          }}
        />
        <div className="flex flex-col gap-1 py-1.5">
          <p
            className="text-sm"
            style={{
              ...typography.small.title,
              color: colors.text.primary
            }}
          >
            {title}
          </p>
          <div className="flex gap-1 text-xs" style={{ color: colors.text.secondary }}>
            <span>{songCount} songs</span>
            <span>•</span>
            <span style={{ color: isNew ? colors.accent.brand : colors.text.secondary }}>
              {timeAgo}
            </span>
          </div>
        </div>
      </div>
      <button
        onClick={onMenuClick}
        className="w-6 h-6 flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
        style={{ color: colors.text.inactive }}
      >
        <MoreHorizontalIcon />
      </button>
    </div>
  );
};

const NewWorkspaceButton = ({ onClick, selectedMode }) => {
  return (
    <div
      onClick={onClick}
      className="flex gap-3 items-center cursor-pointer hover:opacity-80 transition-opacity"
    >
      <div
        className="w-[60px] h-[50px] rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: colors.bg.fog.thin }}
      >
        <div className="w-[26px] h-[26px]">
          <PlusIcon />
        </div>
      </div>
      <div className="flex flex-col gap-1 py-1.5">
        <p
          className="text-sm"
          style={{
            ...typography.small.title,
            color: colors.text.primary
          }}
        >
          {selectedMode === 'Chat' ? 'New Chat' : 'New Workspace'}
        </p>
      </div>
    </div>
  );
};

const Workspaces = ({ hasPlaybar = false, onResize, currentWidth = 30, onWorkspaceClick, workspaces = [], selectedWorkspaceId = null, onPlaySong, onSelectSong, selectedSongTitle, currentSong = null, isPlaying = false, selectedMode = 'Chat', onModeChange, isCreating = false, onCreateWorkspace }) => {
  const [searchValue, setSearchValue] = useState('');
  const [isResizing, setIsResizing] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [showNewWorkspaceCell, setShowNewWorkspaceCell] = useState(false);
  const containerRef = useRef(null);

  // Update selected workspace when selectedWorkspaceId changes from parent
  useEffect(() => {
    if (selectedWorkspaceId !== null) {
      const workspace = workspaces.find(w => w.id === selectedWorkspaceId);
      if (workspace) {
        setSelectedWorkspace(workspace);
      }
    }
  }, [selectedWorkspaceId, workspaces]);

  const handleNewWorkspace = () => {
    setShowNewWorkspaceCell(true);
  };

  const handleConfirmNewWorkspace = (title) => {
    console.log('Creating workspace with title:', title);
    setShowNewWorkspaceCell(false);

    // Generate a unique image for the new workspace
    const images = [
      'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=60&h=50&fit=crop',
      'https://images.unsplash.com/photo-1614854262340-ab1ca7d079c7?w=60&h=50&fit=crop',
      'https://images.unsplash.com/photo-1619983081563-430f63602796?w=60&h=50&fit=crop',
      'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=60&h=50&fit=crop',
      'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=60&h=50&fit=crop',
      'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=60&h=50&fit=crop',
      'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=60&h=50&fit=crop',
      'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=60&h=50&fit=crop'
    ];
    const randomImage = images[Math.floor(Math.random() * images.length)];

    // Create the workspace via parent component but don't navigate to it
    if (onCreateWorkspace) {
      onCreateWorkspace(title, randomImage, [], false); // autoSelect = false
    }
  };

  const handleCancelNewWorkspace = () => {
    setShowNewWorkspaceCell(false);
  };

  const handleWorkspaceMenu = (workspaceId) => {
    console.log('Workspace menu clicked:', workspaceId);
  };

  const handleWorkspaceClick = (workspace) => {
    setSelectedWorkspace(workspace);
    onWorkspaceClick?.(workspace.id);
  };

  const handleBackToList = () => {
    setSelectedWorkspace(null);
    // Clear the selected workspace ID when going back to list
    onWorkspaceClick?.(null);
  };

  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !onResize || !containerRef.current) return;

      // Get the flex container (should be the parent's parent - the main content area)
      const flexContainer = containerRef.current.parentElement?.parentElement;
      if (!flexContainer) return;

      const containerRect = flexContainer.getBoundingClientRect();
      const mouseX = e.clientX;

      // Calculate width from mouse position to right edge
      const newWidthPx = containerRect.right - mouseX;
      const totalWidth = containerRect.width;

      // Calculate min/max percentages based on constraints
      const minWidthPx = 340;
      const minPercent = (minWidthPx / totalWidth) * 100;
      const maxPercent = 50;

      const newWidthPercent = (newWidthPx / totalWidth) * 100;

      // Clamp between min (340px as %) and 50%
      const clampedWidth = Math.max(minPercent, Math.min(maxPercent, newWidthPercent));

      onResize(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, onResize]);

  // If a workspace is selected, show the workspace view
  if (selectedWorkspace) {
    return (
      <div
        ref={containerRef}
        className="relative flex flex-col h-full w-full"
        style={{ backgroundColor: colors.bg.primary }}
      >
        {/* Resize Handle */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-white/10 transition-colors z-10"
          style={{
            backgroundColor: isResizing ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
            width: '4px',
            marginLeft: '-2px'
          }}
          onMouseDown={handleMouseDown}
        />

        {/* Workspace Header */}
        <div className="pt-4 px-4 pb-3">
          <WorkspaceHeader title={selectedWorkspace.title} onBackToList={handleBackToList} selectedMode={selectedMode} />
        </div>

        {/* Filter Nav */}
        <div className="px-4">
          <FilterNav
            activeFilters={[]}
            searchValue=""
            filterCount={0}
            currentSort="newest"
            onFilterClick={() => {}}
            onSearchChange={() => {}}
            onSortChange={() => {}}
            sidePanelOpen={false}
            hideStyles={true}
          />
        </div>

        {/* Workspace Content - Songs */}
        <div
          className="flex-1 overflow-y-auto px-4 pt-4 scrollbar-hide"
          style={{
            paddingBottom: hasPlaybar ? '100px' : '20px'
          }}
        >
          <div className="flex flex-col">
            {/* Show loading cells at the top while creating */}
            {isCreating && (
              <>
                <SongCellLoading />
                <SongCellLoading />
              </>
            )}

            {/* Show existing songs */}
            {selectedWorkspace.songs && selectedWorkspace.songs.length > 0 ? (
              <>
                {selectedWorkspace.songs.map((song, index) => {
                  // Check if this song is currently playing by matching title
                  const isSongPlaying = currentSong && currentSong.title === song.title && isPlaying;
                  const isSongSelected = selectedSongTitle === song.title;

                  return (
                    <SongCell
                      key={index}
                      title={song.title}
                      genres={song.genres}
                      image={song.image}
                      onPlay={() => {
                        console.log('Play song:', song);
                        if (onPlaySong) {
                          onPlaySong({
                            id: index,
                            title: song.title,
                            genres: song.genres,
                            image: song.image,
                            duration: "2:48",
                            status: "active"
                          });
                        }
                      }}
                      onSelect={() => {
                        console.log('Select song:', song);
                        if (onSelectSong) {
                          onSelectSong({
                            title: song.title,
                            genres: song.genres,
                            image: song.image
                          });
                        }
                      }}
                      onChatBubble={() => {
                        console.log('Chat bubble clicked for song:', song);
                        // Select the song
                        if (onSelectSong) {
                          onSelectSong({
                            title: song.title,
                            genres: song.genres,
                            image: song.image
                          });
                        }
                        // Switch to Chat mode
                        if (onModeChange) {
                          onModeChange('Chat');
                        }
                      }}
                      onThumbsUp={() => console.log('Thumbs up:', song)}
                      onThumbsDown={() => console.log('Thumbs down:', song)}
                      onShare={() => console.log('Share:', song)}
                      onAdd={() => console.log('Add:', song)}
                      onMore={() => console.log('More:', song)}
                      isSelected={isSongSelected}
                      isPlaying={isSongPlaying}
                    />
                  );
                })}
              </>
            ) : !isCreating ? (
              <p style={{ ...typography.small.regular, color: colors.text.secondary }}>
                No songs in this workspace yet
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col h-full w-full"
      style={{ backgroundColor: colors.bg.primary }}
    >
      {/* Resize Handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-white/10 transition-colors z-10"
        style={{
          backgroundColor: isResizing ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
          width: '4px',
          marginLeft: '-2px'
        }}
        onMouseDown={handleMouseDown}
      />
      {/* Header with Search */}
      <div
        className="flex flex-col w-full"
        style={{
          backgroundColor: colors.bg.primary
        }}
      >
        <div className="pt-4 px-4">
          <div
            className="flex items-center gap-2 px-3 rounded-full border"
            style={{
              backgroundColor: colors.bg.fog.thin,
              borderColor: colors.bg.secondary,
              height: '40px'
            }}
          >
            <div className="flex items-center justify-center flex-shrink-0" style={{ color: colors.text.inactive, width: '16px', height: '16px' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.94453 11.8891C5.5626 11.8891 4.39303 11.4105 3.43582 10.4532C2.47861 9.49604 2 8.32647 2 6.94453C2 5.5626 2.47861 4.39303 3.43582 3.43582C4.39303 2.47861 5.5626 2 6.94453 2C8.32647 2 9.49604 2.47861 10.4532 3.43582C11.4105 4.39303 11.8891 5.5626 11.8891 6.94453C11.8891 7.50238 11.8003 8.02853 11.6228 8.52298C11.4453 9.01743 11.2044 9.45483 10.9002 9.83518L13.7908 12.7258C13.9303 12.8653 14 13.0428 14 13.2583C14 13.4739 13.9303 13.6513 13.7908 13.7908C13.6513 13.9303 13.4739 14 13.2583 14C13.0428 14 12.8653 13.9303 12.7258 13.7908L9.83518 10.9002C9.45483 11.2044 9.01743 11.4453 8.52298 11.6228C8.02853 11.8003 7.50238 11.8891 6.94453 11.8891ZM6.94453 10.3677C7.8954 10.3677 8.70365 10.0349 9.36926 9.36926C10.0349 8.70365 10.3677 7.8954 10.3677 6.94453C10.3677 5.99366 10.0349 5.18542 9.36926 4.51981C8.70365 3.8542 7.8954 3.52139 6.94453 3.52139C5.99366 3.52139 5.18542 3.8542 4.51981 4.51981C3.8542 5.18542 3.52139 5.99366 3.52139 6.94453C3.52139 7.8954 3.8542 8.70365 4.51981 9.36926C5.18542 10.0349 5.99366 10.3677 6.94453 10.3677Z" fill="currentColor"/>
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-[var(--placeholder-color)]"
              style={{
                color: colors.text.primary,
                ...typography.small.title,
                '--placeholder-color': colors.bg.fog.dense
              }}
            />
          </div>
        </div>
      </div>

      {/* Workspace List */}
      <div
        className="flex-1 overflow-y-auto px-4 pt-4"
        style={{
          paddingBottom: hasPlaybar ? '100px' : '20px'
        }}
      >
        <div className="flex flex-col gap-4">
          {/* New Workspace Cell or Button */}
          {showNewWorkspaceCell ? (
            <NewWorkspaceCell
              onConfirm={handleConfirmNewWorkspace}
              onCancel={handleCancelNewWorkspace}
            />
          ) : (
            <NewWorkspaceButton onClick={handleNewWorkspace} selectedMode={selectedMode} />
          )}

          {/* Workspace Items */}
          {workspaces
            .filter(workspace =>
              workspace.title.toLowerCase().includes(searchValue.toLowerCase())
            )
            .map(workspace => (
              <WorkspaceCell
                key={workspace.id}
                title={workspace.title}
                songCount={workspace.songCount}
                timeAgo={workspace.timeAgo}
                image={workspace.image}
                onMenuClick={() => handleWorkspaceMenu(workspace.id)}
                onClick={() => handleWorkspaceClick(workspace)}
              />
            ))
          }
        </div>
      </div>
    </div>
  );
};

export default Workspaces;